/**
 * useLayoutSync - the one line a surface adds to follow its user across devices.
 *
 * A surface reads its layout SYNCHRONOUSLY from the cache in a `useState`
 * initialiser; that is what stops every page painting on the defaults and then
 * jumping. This hook is the other half: after that first paint it asks the
 * server, and if the server has a newer arrangement it hands it over to be
 * adopted. Nothing here blocks anything - the surface is already usable, and
 * an unreachable server means this simply never calls back.
 *
 * IT ALSO ENROLS THE KEY. `hydrateScoped` is what marks a scope as synced, so
 * calling this hook is what turns on the debounced PUTs for that surface's
 * later writes. A surface that does not call it - or a unit test that never
 * mounts one - keeps the local-only behaviour it has always had.
 *
 * THE STALENESS GUARD is not optional. The key changes when the user id
 * arrives after a first-ever login, and when a per-record surface moves
 * between records without remounting. Both leave a request in flight for the
 * PREVIOUS key, and applying its answer would show one matter's panes on
 * another - the exact bug `usePanelSurface`'s own re-read comment describes,
 * arrived at from the network side.
 */

import { useEffect } from 'react';
import { hydrateScoped } from './layoutStore';

export function useLayoutSync(
  key: string | null | undefined,
  /**
   * Adopt the server's layout. Called at most once per key, and only when the
   * server actually had one - never with `null`, so a caller does not have to
   * distinguish "nothing stored" from "keep what you have".
   */
  adopt: (layout: unknown) => void
): void {
  useEffect(() => {
    if (!key) return undefined;
    let current = true;

    hydrateScoped(key)
      .then((layout) => {
        if (current && layout !== null && layout !== undefined) adopt(layout);
      })
      .catch(() => {
        /* `hydrateScoped` already degrades to the cache; nothing to add. */
      });

    return () => {
      current = false;
    };
    // `adopt` is intentionally not a dependency: callers pass an inline
    // closure, and re-running this on every render would re-fetch the layout
    // on every render. The key is the identity that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export default useLayoutSync;
